"use client";

import { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import DottedMap from "dotted-map";
import Image from "next/image";
//import { useTheme } from "next-themes";

interface MapProps {
  dots?: Array<{
    start: { lat: number; lng: number; label?: string };
    end: { lat: number; lng: number; label?: string };
  }>;
  lineColor?: string;
  /** 弧线绘制动画是否循环重播 */
  loop?: boolean;
  /** 一轮绘制完成后空闲多少秒再重播，默认 4 秒 */
  idleSeconds?: number;
  /** 深色模式：白点半透明地图点、深色底、浅灰标签 */
  dark?: boolean;
}

// dotted-map 构建世界点阵约需 1s CPU：按主题做模块级缓存，避免每次 SSR/渲染重算（首页 TTFB 曾因此达 1.3s）
const svgCache = new Map<string, string>()
function getWorldSvg(theme: "dark" | "light"): string {
  const cached = svgCache.get(theme)
  if (cached) return cached
  const map = new DottedMap({ height: 100, grid: "diagonal" })
  const svg = map.getSVG({
    radius: 0.22,
    color: theme === "dark" ? "#FFFFFF40" : "#00000040",
    shape: "circle",
    backgroundColor: theme === "dark" ? "#020617" : "white",
  })
  svgCache.set(theme, svg)
  return svg
}

export function WorldMap({
  dots = [],
  lineColor = "#0ea5e9",
  loop = false,
  idleSeconds = 4,
  dark = false,
}: MapProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [cycle, setCycle] = useState(0);

  // 循环播放：一轮绘制时长 = 最后一条弧线的 delay + duration，之后空闲 idleSeconds 再重播
  useEffect(() => {
    if (!loop || dots.length === 0) return;
    const drawSeconds = 0.5 * (dots.length - 1) + 1;
    const timer = setInterval(
      () => setCycle((c) => c + 1),
      (drawSeconds + idleSeconds) * 1000
    );
    return () => clearInterval(timer);
  }, [loop, idleSeconds, dots.length]);
  const theme = dark ? "dark" : "light";
  const labelColor = dark ? "#94a3b8" : "#4b5563";

  const svgMap = getWorldSvg(theme);

  const projectPoint = (lat: number, lng: number) => {
    const x = (lng + 180) * (800 / 360);
    const y = (90 - lat) * (400 / 180);
    return { x, y };
  };

  const createCurvedPath = (
    start: { x: number; y: number },
    end: { x: number; y: number }
  ) => {
    const midX = (start.x + end.x) / 2;
    const midY = Math.min(start.y, end.y) - 50;
    return `M ${start.x} ${start.y} Q ${midX} ${midY} ${end.x} ${end.y}`;
  };

  return (
    <div className={`w-full aspect-[2/1] rounded-lg relative font-sans ${dark ? "bg-slate-950" : "dark:bg-black bg-white"}`}>
      <Image
        src={`data:image/svg+xml;utf8,${encodeURIComponent(svgMap)}`}
        className="h-full w-full [mask-image:linear-gradient(to_bottom,transparent,white_10%,white_90%,transparent)] pointer-events-none select-none"
        alt="world map"
        height="495"
        width="1056"
        draggable={false}
      />
      <svg
        ref={svgRef}
        viewBox="0 0 800 400"
        className="w-full h-full absolute inset-0 pointer-events-none select-none"
      >
        {dots.map((dot, i) => {
          const startPoint = projectPoint(dot.start.lat, dot.start.lng);
          const endPoint = projectPoint(dot.end.lat, dot.end.lng);
          return (
            <g key={`path-group-${cycle}-${i}`}>
              <motion.path
                d={createCurvedPath(startPoint, endPoint)}
                fill="none"
                stroke="url(#path-gradient)"
                strokeWidth="1"
                
                
                initial={{
                  pathLength: 0,
                }}
                animate={{
                  pathLength: 1,
                  
                }}
                transition={{
                  duration: 1,
                  delay: 0.5 * i,
                  ease: "easeOut",
                }}
                key={`start-upper-${i}`}
              ></motion.path>
            </g>
          );
        })}

        <defs>
          <linearGradient id="path-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="white" stopOpacity="0" />
            <stop offset="5%" stopColor={lineColor} stopOpacity="1" />
            <stop offset="95%" stopColor={lineColor} stopOpacity="1" />
            <stop offset="100%" stopColor="white" stopOpacity="0" />
          </linearGradient>
        </defs>

        {dots.map((dot, i) => (
          <g key={`points-group-${i}`}>
            <g key={`start-${i}`}>
              {/* 呼吸光环（节点生气） */}
              <circle
                cx={projectPoint(dot.start.lat, dot.start.lng).x}
                cy={projectPoint(dot.start.lat, dot.start.lng).y}
                r="6"
                fill={lineColor}
                className="animate-breathe-glow"
              />
              <circle
                cx={projectPoint(dot.start.lat, dot.start.lng).x}
                cy={projectPoint(dot.start.lat, dot.start.lng).y}
                r="2"
                fill={lineColor}
              />
              {dot.start.label && (
                <text
                  x={projectPoint(dot.start.lat, dot.start.lng).x}
                  y={projectPoint(dot.start.lat, dot.start.lng).y - 6}
                  textAnchor="middle"
                  fontSize="9"
                  fill={labelColor}
                  fontWeight="600"
                >
                  {dot.start.label}
                </text>
              )}
              <circle
                cx={projectPoint(dot.start.lat, dot.start.lng).x}
                cy={projectPoint(dot.start.lat, dot.start.lng).y}
                r="2"
                fill={lineColor}
                opacity="0.5"
              >
                <animate
                  attributeName="r"
                  from="2"
                  to="8"
                  dur="1.5s"
                  begin="0s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  from="0.5"
                  to="0"
                  dur="1.5s"
                  begin="0s"
                  repeatCount="indefinite"
                />
              </circle>
            </g>
            <g key={`end-${i}`}>
              <circle
                cx={projectPoint(dot.end.lat, dot.end.lng).x}
                cy={projectPoint(dot.end.lat, dot.end.lng).y}
                r="6"
                fill={lineColor}
                className="animate-breathe-glow"
              />
              <circle
                cx={projectPoint(dot.end.lat, dot.end.lng).x}
                cy={projectPoint(dot.end.lat, dot.end.lng).y}
                r="2"
                fill={lineColor}
              />
              {dot.end.label && (
                <text
                  x={projectPoint(dot.end.lat, dot.end.lng).x}
                  y={projectPoint(dot.end.lat, dot.end.lng).y - 6}
                  textAnchor="middle"
                  fontSize="9"
                  fill={labelColor}
                  fontWeight="600"
                >
                  {dot.end.label}
                </text>
              )}
              <circle
                cx={projectPoint(dot.end.lat, dot.end.lng).x}
                cy={projectPoint(dot.end.lat, dot.end.lng).y}
                r="2"
                fill={lineColor}
                opacity="0.5"
              >
                <animate
                  attributeName="r"
                  from="2"
                  to="8"
                  dur="1.5s"
                  begin="0s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  from="0.5"
                  to="0"
                  dur="1.5s"
                  begin="0s"
                  repeatCount="indefinite"
                />
              </circle>
            </g>
          </g>
        ))}
      </svg>
    </div>
  );
}
